import { useCallback } from 'react';
import { newId } from '@battleops/localstore';
import { localToday, parseQuickTitle, parseQuickEvent, reminderTask, commands } from '@battleops/shared';
import { commitWrites } from '@battleops/client/src/data/commit';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useProjects } from '@battleops/client/src/context/ProjectContext';

/** What one typed line becomes — the phone's grammar: "remind …" = a reminder task, "call/meet … 3pm" = a
 *  calendar event (when asked for), anything else = a task (~45m, $120, !, #tag, " :: milk, eggs" = its list). */
export function previewLine(value, { allowEvent = true } = {}) {
  const v = String(value || '').trim();
  if (!v) return null;
  const today = localToday();
  if (/^remind/i.test(v)) {
    const rem = reminderTask(parseQuickEvent(v, { today }));
    if (rem) return { type: 'reminder', ...rem };
  }
  if (allowEvent) {
    const ev = parseQuickEvent(v, { today });
    if (ev.type === 'event' && ev.start) return { type: 'event', ...ev };
  }
  return { type: 'task', ...parseQuickTitle(v) };
}

export function useQuickAdd() {
  const { addTask } = useTasks();
  const { projects } = useProjects();
  // date: undefined = today, null = backlog (no date), 'YYYY-MM-DD' = that day. fields = extra task fields (the department
  // chip's pick, v0.1.242 — otherwise TaskContext infers it).
  return useCallback((value, { projectId = null, sectionId = null, parentId = null, date, allowEvent = true, fields = null } = {}) => {
    const p = previewLine(value, { allowEvent });
    if (!p || !p.title) return null;
    if (p.type === 'event') {
      const r = commands.addEvent({ projects, newId }, { title: p.title, kind: p.kind || 'other', date: p.date, start: p.start, end: p.end || null, projectId, tags: p.tags || [] });
      commitWrites(r.writes).catch((e) => console.error('add event failed', e));
      return { type: 'event', event: r.event };
    }
    const task = addTask({
      title: p.title,
      projectId: projectId || null,
      sectionId: sectionId || null,
      parentId: parentId || null,
      scheduledDate: p.type === 'reminder' && !date ? p.scheduledDate : date === undefined ? localToday() : date,
      remindTime: p.remindTime ?? null,
      remindStyle: p.remindStyle ?? null,
      priority: p.priority || 'medium',
      estimatedMinutes: p.estimatedMinutes ?? null,
      moneyEarned: p.moneyEarned ?? 0,
      tags: p.tags || [],
      ...(p.list ? { list: p.list } : {}),
      ...(fields || {}),
    });
    return { type: p.type, task };
  }, [addTask, projects]);
}
